import { emitEvent, EVENTS } from './events';

// Construct WebSocket URL from API URL
const WS_URL = (() => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const baseUrl = apiUrl.replace(/\/api$/, ''); // Remove trailing /api
  const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
  return `${wsProtocol}://${new URL(baseUrl).host}/ws`;
})();

class WebSocketClient {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second delay
  }

  connect(token) {
    if (!token) return;

    // Close existing connection if any
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    try {
      const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}`;
      this.ws = new WebSocket(wsUrl);

      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
      }, 5000); // 5 second timeout

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle both notification and population updates
          if (data.timestamp && (data.type === 'NOTIFICATION_UPDATE' || data.type === 'POPULATION_UPDATE')) {
            emitEvent(EVENTS.NOTIFICATION_UPDATE);
          }
        } catch (error) {
          // Try to reconnect on message error
          this.ws.close();
        }
      };

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        
        // Only attempt reconnection if the closure wasn't clean
        if (!event.wasClean) {
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
            setTimeout(() => {
              this.reconnectAttempts++;
              this.connect(token); // Pass token during reconnect
            }, delay); // Exponential backoff
          }
        }
      };

      this.ws.onerror = () => {
        // Don't close here, let onclose handle reconnection
      };
    } catch (error) {
      // Attempt to reconnect after error in creation
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
          setTimeout(() => {
            this.reconnectAttempts++;
            this.connect(token); // Pass token during reconnect
          }, delay);
      }
    }
  }

  addListener(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push(callback);
  }

  removeListener(type, callback) {
    if (!this.listeners.has(type)) return;
    const listeners = this.listeners.get(type);
    const index = listeners.indexOf(callback);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  }

  close() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

export const wsClient = new WebSocketClient();
